import { useFetcher } from '@remix-run/react';
import { useEffect, useRef, useState } from 'react';

const IMPERSONATION_CHANNEL = 'speaker-admin:impersonation';

export function ImpersonationBanner({
    isImpersonating,
    impersonatedUserId,
    userName,
}: {
    isImpersonating: boolean;
    impersonatedUserId: string | null;
    userName?: string;
}) {
    const fetcher = useFetcher();
    const [ showOverlay, setShowOverlay ] = useState(false);

    // Use a single BroadcastChannel instance for both sending and listening.
    // The spec guarantees messages are NOT delivered back to the instance that
    // posted them, which prevents this tab from showing the overlay on its own
    // broadcasts.
    const channelRef = useRef<BroadcastChannel | null>(null);
    useEffect(() => {
        const bc = new BroadcastChannel(IMPERSONATION_CHANNEL);
        bc.onmessage = () => setShowOverlay(true);
        channelRef.current = bc;
        return () => {
            bc.close();
            channelRef.current = null;
        };
    }, []);

    // Broadcast to other tabs when impersonation state changes, or on initial
    // mount while impersonating (covers the case where a new tab opens via the
    // /admin/impersonate redirect — there's no state *change* in this tab, but
    // other tabs need to know the cookie now points to a different user).
    const isFirstRender = useRef(true);
    const prevState = useRef({ isImpersonating, impersonatedUserId });
    useEffect(() => {
        const prev = prevState.current;
        const changed = prev.isImpersonating !== isImpersonating || prev.impersonatedUserId !== impersonatedUserId;

        if (changed || (isFirstRender.current && isImpersonating)) {
            channelRef.current?.postMessage('impersonation-changed');
        }
        isFirstRender.current = false;
        prevState.current = { isImpersonating, impersonatedUserId };
    }, [ isImpersonating, impersonatedUserId ]);

    return (
        <>
            {showOverlay && (
                <div className='fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm'>
                    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md text-center space-y-4'>
                        <p className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                            ⚠ Impersonation Changed
                        </p>
                        <p className='text-sm text-gray-600 dark:text-gray-400'>
                            The impersonation session was changed in another tab. Please reload to continue.
                        </p>
                        <button
                            type='button'
                            onClick={() => {
                                // Assigning the href first ensures we navigate to root,
                                // and reload() forces a full server round-trip even if
                                // we're already on '/'.
                                if (window.location.pathname !== '/') {
                                    window.location.href = '/';
                                } else {
                                    window.location.reload();
                                }
                            }}
                            className='bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm px-6 py-2 rounded transition-colors'
                        >
                            Reload
                        </button>
                    </div>
                </div>
            )}
            {isImpersonating && (
                <>
                    <div className='bg-amber-500 text-black text-center text-sm font-medium h-8 px-4 flex items-center justify-center gap-3 z-50 fixed w-full'>
                        <span>⚠ Impersonating{userName ? `: ${userName}` : ''}</span>
                        <fetcher.Form method='post' action='/admin/end-impersonate' className='inline'>
                            <button
                                type='submit'
                                className='bg-black/20 hover:bg-black/30 text-black font-semibold text-xs px-3 py-0.5 rounded transition-colors'
                            >
                                Stop
                            </button>
                        </fetcher.Form>
                    </div>
                    <div className='h-8' /> {/* spacer to prevent content being hidden behind the fixed banner */}
                </>
            )}
        </>
    );
}
